import { Form, Input, Button, message } from 'antd';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Fingerprint2 from 'fingerprintjs2';
import { createData } from '../api/api';
import '../styles/Login.css';

const Login = ({ setAuthenticated }) => {
    const { t } = useTranslation();
    const [step, setStep] = useState(1); // 1: Phone number, 2: SMS code, 3: Set PIN, 4: Enter PIN
    const [phone, setPhone] = useState('');
    const [deviceId, setDeviceId] = useState(null);
    const [confirmId, setConfirmId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [authStatus, setAuthStatus] = useState(null); // NEW_USER, NEW_DEVICE, PIN_USER
    const [pin, setPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [refreshPin, setRefreshPin] = useState(false);

    // Get or generate deviceId
    useEffect(() => {
        const storedDeviceId = localStorage.getItem('deviceId');
        if (storedDeviceId) {
            setDeviceId(storedDeviceId);
        } else {
            Fingerprint2.get((components) => {
                const values = components.map((component) => component.value);
                const newDeviceId = Fingerprint2.x64hash128(values.join(''), 31);
                setDeviceId(newDeviceId);
                localStorage.setItem('deviceId', newDeviceId);
            });
        }
    }, []);

    // Send phone number
    const onSendCode = async (values) => {
        try {
            setIsLoading(true);
            const formattedPhone = values.phone.substring(1); // Remove +998
            setPhone(formattedPhone);
            const response = await createData('/auth/login/send-sms-code', {
                phone: formattedPhone,
                deviceId,
                refreshPin,
            });
            if (response.data.success) {
                message.success(t('login.codeSent'));
                setConfirmId(response.data.data.confirmId);
                setAuthStatus(response.data.data.authStatus);
                if (response.data.data.authStatus === 'PIN_USER') {
                    setStep(4); // Go to PIN entry step
                } else {
                    setStep(2); // Go to SMS code verification step
                }
            } else {
                alert(response.data.error.text);
                message.error(response.data.error.text);
            }
        } catch (error) {
            alert(t('login.error'));
            message.error(t('login.error'));
        } finally {
            setRefreshPin(false);
            setIsLoading(false);
        }
    };

    // Verify SMS code
    const onVerifyCode = async (values) => {
        try {
            setIsLoading(true);
            const response = await createData('/auth/login/confirm-sms-code', {
                confirmId,
                code: values.code,
                deviceId
            });
            if (response.data.success) {
                if (response.data.data === 'NEW_USER') {
                    setStep(3); // Go to set PIN step
                } else if (authStatus === 'NEW_DEVICE' || authStatus === 'PIN_USER') {
                    setStep(4); // Go to PIN entry step
                }
            } else {
                alert(response.data.error.text);
                message.error(response.data.error.text);
            }
        } catch (error) {
            alert(t('login.error'));
            message.error(t('login.error'));
        } finally {
            setIsLoading(false);
        }
    };

    // Set PIN
    const onSetPin = async () => {
        if (pin !== confirmPin) {
            message.error(t('login.pinsDoNotMatch'));
            return;
        }
        try {
            setIsLoading(true);
            const response = await createData('/auth/login/set-password', {
                confirmId,
                code: pin,
            });
            if (response.data.success) {
                message.success(t('login.pinSetSuccess'));
                setStep(1); // Return to login page
            } else {
                alert(response.data.error.text);
                message.error(response.data.error.text);
            }
        } catch (error) {
            alert(t('login.error'));
            message.error(t('login.error'));
        } finally {
            setIsLoading(false);
        }
    };

    // Login with PIN
    const onLoginWithPin = async () => {
        try {
            setIsLoading(true);
            const response = await createData('/auth/login/get-token', {
                code: pin,
                deviceId,
                phone,
            });
            if (response.data.success) {
                const accessToken = response.data.data.access_token;
                localStorage.setItem('accessToken', accessToken);
                message.success(t('login.loginSuccess'));
                window.location.reload();
            } else {
                alert(response.data.error.text);
                message.error(response.data.error.text);
            }
        } catch (error) {
            alert(t('login.error'));
            message.error(t('login.error'));
        } finally {
            setIsLoading(false);
        }
    };

    const onRefreshPin = async () => {
        setStep(1);
        setRefreshPin(true);
    };

    return (
        <div className="login-container">
            {step === 1 && (
                <Form className="login-form" onFinish={onSendCode}>
                    <h2 className="login-title">{t('login.title')}</h2>
                    <Form.Item
                        name="phone"
                        rules={[
                            { required: true, message: t('login.phoneRequired') },
                            {
                                pattern: /^\+998\d{9}$/,
                                message: t('login.phonePattern'),
                            },
                        ]}
                    >
                        <Input
                            placeholder={t('login.phonePlaceholder')}
                            maxLength={13}
                            defaultValue="+998"
                            onChange={(e) => {
                                const value = e.target.value;
                                if (!value.startsWith('+998')) {
                                    e.target.value = '+998';
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" className="login-form-button" loading={isLoading}>
                            {t('login.sendCode')}
                        </Button>
                    </Form.Item>
                </Form>
            )}

            {step === 2 && (
                <Form className="login-form" onFinish={onVerifyCode}>
                    <h2 className="login-title">{t('login.verifyCodeTitle')}</h2>
                    <Form.Item
                        name="code"
                        rules={[{ required: true, message: t('login.codeRequired') }]}
                    >
                        <Input placeholder={t('login.codePlaceholder')} maxLength={6} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" className="login-form-button" loading={isLoading}>
                            {t('login.verify')}
                        </Button>
                        <Button type="link" onClick={() => setStep(1)} style={{ marginLeft: 10 }}>
                            {t('login.otherNumber')}
                        </Button>
                    </Form.Item>
                </Form>
            )}

            {step === 3 && (
                <div className="login-form">
                    <h2 className="login-title">{t('login.setPinTitle')}</h2>
                    <Input
                        placeholder={t('login.pinPlaceholder')}
                        maxLength={4}
                        value={pin}
                        onChange={(e) => setPin(e.target.value)}
                        style={{ marginBottom: 10 }}
                    />
                    <Input
                        placeholder={t('login.confirmPinPlaceholder')}
                        maxLength={4}
                        value={confirmPin}
                        onChange={(e) => setConfirmPin(e.target.value)}
                        style={{ marginBottom: 10 }}
                    />
                    <Button type="primary" onClick={onSetPin} className="login-form-button" loading={isLoading}>
                        {t('login.confirmPin')}
                    </Button>
                </div>
            )}

            {step === 4 && (
                <div className="login-form">
                    <h2 className="login-title">{t('login.loginWithPinTitle')}</h2>
                    <Input
                        placeholder={t('login.pinPlaceholder')}
                        maxLength={4}
                        value={pin}
                        onChange={(e) => setPin(e.target.value)}
                        style={{ marginBottom: 10 }}
                    />
                    <Button type="primary" onClick={onLoginWithPin} className="login-form-button" loading={isLoading}>
                        {t('login.login')}
                    </Button>
                    <Button type="link" onClick={onRefreshPin} style={{ marginLeft: 10 }}>
                        {t('login.changePin')}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default Login;